/* eslint-disable complexity */
import {
	classifyCombinablePromotions,
	filterValidCombinablePromotions,
	getPromoTypeFromName,
} from '@/components/blocks/FreeGiftV2/utils';
import { CombinablePromotion } from '@/data/types/Promotions';
import {
	GIFT_TYPE,
	GiftType,
	MULTI_FREE_GIFT_PROMO,
} from 'presentation/events/constants/eventConstants';
import { PromotionDetails } from 'presentation/events/hooks/useGetPromotionDetails';
import {
	CheckoutEventBase,
	CheckoutEventItem,
	ProductWithCurrentOrderDataType,
} from 'presentation/events/types/checkoutEventsTypes';
import { getItemPartNumber } from './productUtils';

export const applyFreeGiftPromotion = <T extends CheckoutEventItem>(
	props: CheckoutEventBase<T> & { physicalStoreId: string }
): CheckoutEventBase<T> => {
	const items = props.items;

	const productsWithGiftPromotion = items.filter((item) => {
		const promotionData = item.promotionDetails?.promotionData;
		return promotionData?.field1 === MULTI_FREE_GIFT_PROMO;
	});

	if (productsWithGiftPromotion.length === 0) return props;

	const freeGiftCatentryIds: Set<string> = new Set();
	const promoCodeSet = new Set<string>();

	const giftCatentryToCode = new Map<string, string>();
	const triggerPartNumberToCode = new Map<string, string>();

	productsWithGiftPromotion.forEach((item) => {
		const promo = item.promotionDetails?.promotionData;

		if (!promo) return;
		if (promo.active !== true) return;
		if (promo.physicalStores?.find((store) => store === props.physicalStoreId) === undefined)
			return;
		if (!promo.giftCatentries) return;
		if (!promo.PromotionName) return;

		promo.giftCatentries.forEach((catentry) => freeGiftCatentryIds.add(catentry));
		promoCodeSet.add(promo.PromotionName);

		const promoThdCode = classifyCombinablePromotions(
			item.promotionDetails?.combinablePromotions as CombinablePromotion[] | undefined
		);

		if (promoThdCode) {
			triggerPartNumberToCode.set(getItemPartNumber(item.product), promoThdCode);
			promo.giftCatentries.forEach((catentry) => giftCatentryToCode.set(catentry, promoThdCode));
		}
	});

	const freeGiftPartNumbers = new Set<string>();
	const productPromotionCodes = new Map<string, Set<string>>();

	items.forEach((item) => {
		const partNumber = getItemPartNumber(item.product);

		if (!item.product.adjustments) return;
		if (!freeGiftCatentryIds.has(item.product.id)) return;

		const codes = item.product.adjustments
			.map((adj) => adj.code)
			.filter((code): code is string => !!code && promoCodeSet.has(code));

		if (codes.length === 0) return;

		freeGiftPartNumbers.add(partNumber);
		productPromotionCodes.set(partNumber, new Set(codes));
	});

	const newItems = items.map((item) => {
		const partNumber = getItemPartNumber(item.product);
		const isGiftItem = freeGiftPartNumbers.has(partNumber);

		const promoThdCode =
			(isGiftItem ? giftCatentryToCode.get(item.product.id) : undefined) ??
			triggerPartNumberToCode.get(partNumber);

		if (!isGiftItem && !promoThdCode) return item;

		const product = item.product as ProductWithCurrentOrderDataType;
		const promoCodes = isGiftItem ? productPromotionCodes.get(partNumber) : undefined;

		const newProduct: ProductWithCurrentOrderDataType = {
			...product,
			...(isGiftItem ? { isGift: true } : {}),
			...(promoCodes ? { freeGiftPromotionCodes: [...promoCodes] } : {}),
			...(promoThdCode ? { promoThdCode } : {}),
		};

		return {
			...item,
			product: newProduct,
		};
	});

	return {
		...props,
		items: newItems,
	};
};

export type PredictFreeGiftCartItem = {
	partNumber: string;
	productId: string;
	quantity: string;
};

export type PredictFreeGiftResult = {
	isGift: boolean;
	freeQty: string | undefined;
	promoThdCode?: string;
};

export type PredictFreeGiftForRemoveParams = {
	productCatentryId: string;
	removeQuantity: number;
	cartItems: PredictFreeGiftCartItem[];
	cartItemsPromotionDetails: Map<string, PromotionDetails>;
	physicalStoreId: string;
};

export const predictFreeGiftForRemoveFromCart = (
	params: PredictFreeGiftForRemoveParams
): PredictFreeGiftResult => {
	const {
		productCatentryId,
		removeQuantity,
		cartItems,
		cartItemsPromotionDetails,
		physicalStoreId,
	} = params;

	const processedPromos = new Set<string>();

	const { bestFreeQty, bestTotalGiftQty, bestPromoDetails } = Array.from(
		cartItemsPromotionDetails.values()
	).reduce<{ bestFreeQty: number; bestTotalGiftQty: number; bestPromoDetails?: PromotionDetails }>(
		(best, promoDetails) => {
			if (!promoDetails.promotionData) return best;

			const promoName = promoDetails.promotionData.PromotionName;
			if (promoName && processedPromos.has(promoName)) return best;
			if (promoName) processedPromos.add(promoName);

			const promotionData = promoDetails.promotionData;
			if (promotionData.field1 !== MULTI_FREE_GIFT_PROMO) return best;
			if (promotionData.active !== true) return best;
			if (!promotionData.physicalStores?.includes(physicalStoreId)) return best;

			const giftCatentries = promotionData.giftCatentries;
			if (!giftCatentries?.includes(productCatentryId)) return best;

			const baseQty = promotionData.baseQty;
			const giftQty = promotionData.giftQty;
			if (!baseQty || !giftQty) return best;

			const relatedCatentries = promotionData.relatedCatentries;
			const parentCatentriesSet = new Set(relatedCatentries ?? []);
			const totalParentQtyInCart = cartItems
				.filter((item) => parentCatentriesSet.has(item.productId))
				.reduce((sum, item) => sum + parseFloat(item.quantity), 0);

			const promoApplications = Math.floor(totalParentQtyInCart / baseQty);
			if (promoApplications <= 0) return best;

			const giftCatentriesSet = new Set(giftCatentries);
			const totalGiftQtyInCart = cartItems
				.filter((item) => giftCatentriesSet.has(item.productId))
				.reduce((sum, item) => sum + parseFloat(item.quantity), 0);

			const maxBlocks = promoApplications;
			const existingBlocks = Math.min(Math.floor(totalGiftQtyInCart / giftQty), maxBlocks);
			const existingFree = existingBlocks * giftQty;

			if (existingFree > best.bestFreeQty) {
				return {
					bestFreeQty: existingFree,
					bestTotalGiftQty: totalGiftQtyInCart,
					bestPromoDetails: promoDetails,
				};
			}

			return best;
		},
		{ bestFreeQty: 0, bestTotalGiftQty: 0 }
	);

	if (bestFreeQty <= 0) {
		return { isGift: false, freeQty: undefined };
	}

	const nonFreeQty = bestTotalGiftQty - bestFreeQty;
	const freeQtyBeingRemoved = Math.max(0, removeQuantity - nonFreeQty);

	if (freeQtyBeingRemoved <= 0) {
		return { isGift: false, freeQty: undefined };
	}

	const promoThdCode = classifyCombinablePromotions(
		bestPromoDetails?.combinablePromotions as CombinablePromotion[] | undefined
	);

	return {
		isGift: true,
		freeQty: String(freeQtyBeingRemoved),
		promoThdCode,
	};
};

export const getGiftType = ({
	partNumber,
	combinablePromotions,
}: {
	partNumber: string | undefined;
	combinablePromotions: CombinablePromotion[] | null | undefined;
}): GiftType | undefined => {
	if (!partNumber) return undefined;

	const pgPromotions = filterValidCombinablePromotions(combinablePromotions ?? []).filter(
		(promo) => getPromoTypeFromName(promo.PromotionName) !== undefined
	);

	const isTrigger = pgPromotions.some((promo) =>
		(promo.purchase.triggerSKUs ?? []).some(({ sku }) => sku === partNumber)
	);
	if (isTrigger) return GIFT_TYPE.TRIGGER;

	const isGift = pgPromotions.some((promo) =>
		(promo.reward.targetSKUs ?? []).some(({ sku }) => sku === partNumber)
	);
	if (isGift) return GIFT_TYPE.GIFT;

	return undefined;
};
